import { useState, useRef } from 'react'
import { Plus, Search, Edit2, Trash2, X, BookOpen, Upload, FolderOpen, CheckSquare, Tag, Link as LinkIcon } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { getStatusColor, formatDateShort, formatFileSize, generateId, getFileTypeIcon } from '../utils/helpers'

const emptyKB = {
  name: '', category: 'Listing Rules', content: '', fileType: 'Markdown',
  version: 'v1.0', status: '草稿', agentIds: [], owner: '', remark: ''
}

const categories = [
  'Listing Rules', 'A10 Rules', 'IDQ Rules', 'Rufus Rules', 'Cosmo Rules', 'Alexa Rules',
  'Ads Rules', 'Visual Rules', 'QA Rules', 'Video Rules', 'Compliance Rules',
  'SOP', 'Cases', 'Review Library', 'Raw Uploads', 'Archive'
]
const fileTypes = ['Markdown', 'Google Drive', 'PDF', 'Excel', 'Doc', 'Link']
const statuses = ['草稿', '主用', '废弃']

export default function KnowledgeBase() {
  const { knowledgeBases, setKnowledgeBases, kbFiles, setKbFiles, agents, canEdit } = useApp()
  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showFileDetail, setShowFileDetail] = useState(null)
  const [editingKB, setEditingKB] = useState(null)
  const [form, setForm] = useState(emptyKB)
  const [dragOver, setDragOver] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const fileInputRef = useRef(null)

  const allItems = [
    ...knowledgeBases.map(k => ({ ...k, itemType: 'kb' })),
    ...kbFiles.map(f => ({ ...f, itemType: 'file', name: f.fileName }))
  ]

  const filtered = allItems.filter(k => {
    const matchSearch = k.name.toLowerCase().includes(search.toLowerCase()) || (k.summary || '').toLowerCase().includes(search.toLowerCase())
    const matchCategory = !filterCategory || k.category === filterCategory
    const matchStatus = !filterStatus || k.status === filterStatus
    return matchSearch && matchCategory && matchStatus
  })

  const openModal = (kb = null) => {
    if (kb) { setEditingKB(kb); setForm({ ...kb }) }
    else { setEditingKB(null); setForm({ ...emptyKB, id: generateId(), updatedAt: new Date().toISOString() }) }
    setShowModal(true)
  }

  const save = () => {
    if (!form.name) return alert('请输入名称')
    const now = new Date().toISOString()
    if (editingKB) { setKnowledgeBases(knowledgeBases.map(k => k.id === editingKB.id ? { ...form, updatedAt: now } : k)) }
    else { setKnowledgeBases([...knowledgeBases, { ...form, id: generateId(), updatedAt: now }]) }
    setShowModal(false)
  }

  const remove = (id, type) => {
    if (!confirm('确定删除？')) return
    if (type === 'kb') setKnowledgeBases(knowledgeBases.filter(k => k.id !== id))
    else setKbFiles(kbFiles.filter(k => k.id !== id))
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    processFiles(files)
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    processFiles(files)
  }

  const processFiles = (files) => {
    const newFiles = files.map(file => ({
      id: generateId(), fileName: file.name,
      fileType: file.name.split('.').pop().toUpperCase(),
      fileSize: file.size, uploadedBy: 'Leo',
      uploadedAt: new Date().toISOString(), category: 'Raw Uploads',
      aiSuggestedCategory: '', agentIds: [], stepIds: [],
      summary: '', keyRules: '', riskPoints: '', tags: [],
      version: 'v1.0', status: '草稿', isParsed: false, isBoundToAgent: false
    }))
    setKbFiles([...newFiles, ...kbFiles])
  }

  const toggleSelectFile = (id) => {
    setSelectedFiles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const batchSetStatus = (status) => {
    setKbFiles(kbFiles.map(f => selectedFiles.includes(f.id) ? { ...f, status } : f))
    setSelectedFiles([])
  }

  const batchDelete = () => {
    if (!confirm(`确定删除 ${selectedFiles.length} 个文件？`)) return
    setKbFiles(kbFiles.filter(f => !selectedFiles.includes(f.id)))
    setSelectedFiles([])
  }

  const getAgentNames = (ids) => {
    if (!ids || ids.length === 0) return '-'
    return agents.filter(a => ids.includes(a.id)).map(a => a.name).join(', ')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">知识库 / 规则库中心</h2>
        {canEdit() && (
          <div className="flex gap-2">
            <button onClick={() => fileInputRef.current?.click()} className="btn-primary flex items-center gap-2">
              <Upload size={16} /> 上传文件
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
            <button onClick={() => openModal()} className="btn-secondary flex items-center gap-2">
              <Plus size={16} /> 手动新增
            </button>
          </div>
        )}
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`card p-6 border-2 border-dashed text-center transition-colors ${dragOver ? 'border-primary-500 bg-primary-50' : 'border-slate-300'}`}
      >
        <Upload size={32} className={`mx-auto mb-2 ${dragOver ? 'text-primary-600' : 'text-slate-400'}`} />
        <p className="text-sm text-slate-600 font-medium">拖拽文件到此处上传</p>
        <p className="text-xs text-slate-400 mt-1">支持 PDF / Word / Excel / CSV / Markdown / TXT / HTML / JSON / 图片 / ZIP</p>
        <p className="text-xs text-slate-400">支持 Google Drive 链接、Amazon 页面链接、网页 URL（第一版先登记）</p>
        <button onClick={() => fileInputRef.current?.click()} className="mt-2 text-xs text-primary-600 hover:underline">或点击选择文件</button>
      </div>

      {/* Batch Operations */}
      {selectedFiles.length > 0 && (
        <div className="card p-3 bg-amber-50 border-amber-200 flex items-center justify-between">
          <span className="text-sm text-amber-800">已选择 {selectedFiles.length} 项</span>
          <div className="flex gap-2">
            <button onClick={() => batchSetStatus('主用')} className="px-2 py-1 bg-white border border-amber-300 text-amber-700 rounded text-xs">批量设为 主用</button>
            <button onClick={() => batchSetStatus('废弃')} className="px-2 py-1 bg-white border border-amber-300 text-amber-700 rounded text-xs">批量设为 废弃</button>
            <button onClick={batchDelete} className="px-2 py-1 bg-red-50 border border-red-300 text-red-700 rounded text-xs">批量删除</button>
            <button onClick={() => setSelectedFiles([])} className="px-2 py-1 text-slate-500 text-xs">取消</button>
          </div>
        </div>
      )}

      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="搜索知识库或文件..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select w-40" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">全部分类</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="select w-32" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">全部状态</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(item => (
          <div key={item.id} className="card p-4 card-hover">
            <div className="flex items-start gap-3">
              {item.itemType === 'file' && (
                <input type="checkbox" checked={selectedFiles.includes(item.id)} onChange={() => toggleSelectFile(item.id)} className="mt-1" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {item.itemType === 'file' && <span className="text-lg">{getFileTypeIcon(item.fileType)}</span>}
                  {item.itemType === 'kb' && <BookOpen size={16} className="text-primary-600" />}
                  <h3 className="font-semibold text-slate-800">{item.name}</h3>
                  <span className={`badge ${getStatusColor(item.status)}`}>{item.status}</span>
                  <span className="badge bg-slate-100 text-slate-600">{item.category}</span>
                  {item.itemType === 'file' && <span className="badge bg-slate-100 text-slate-600">{item.fileType}</span>}
                  {item.itemType === 'file' && <span className="text-xs text-slate-400">{formatFileSize(item.fileSize)}</span>}
                  {item.version && <span className="text-xs text-slate-400">{item.version}</span>}
                  {item.itemType === 'file' && !item.isParsed && <span className="badge bg-amber-100 text-amber-800 text-[10px]">待解析</span>}
                </div>
                {item.summary && <p className="text-sm text-slate-500 mb-1">摘要: {item.summary}</p>}
                {item.keyRules && <p className="text-sm text-slate-500 mb-1">关键规则: {item.keyRules}</p>}
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  {item.itemType === 'file' ? (
                    <>
                      <span>上传人: {item.uploadedBy}</span>
                      <span>{formatDateShort(item.uploadedAt)}</span>
                      <span>绑定Agent: {getAgentNames(item.agentIds)}</span>
                      {item.tags?.length > 0 && <span>标签: {item.tags.join(', ')}</span>}
                    </>
                  ) : (
                    <>
                      <span>负责人: {item.owner}</span>
                      <span>{formatDateShort(item.updatedAt)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {item.itemType === 'file' && (
                  <button onClick={() => setShowFileDetail(item)} className="p-1.5 hover:bg-slate-100 rounded" title="详情"><LinkIcon size={14} className="text-slate-500" /></button>
                )}
                {canEdit() && (
                  <>
                    <button onClick={() => openModal(item)} className="p-1.5 hover:bg-slate-100 rounded"><Edit2 size={14} className="text-slate-500" /></button>
                    <button onClick={() => remove(item.id, item.itemType)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-500" /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && <div className="card p-8 text-center text-slate-400">暂无数据</div>}

      {/* KB Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingKB ? '编辑知识库' : '新增知识库'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">名称</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">分类</label>
                <select className="select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">文件类型</label>
                <select className="select" value={form.fileType} onChange={e => setForm({ ...form, fileType: e.target.value })}>
                  {fileTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">版本</label><input className="input" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">状态</label>
                <select className="select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">负责人</label><input className="input" value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} /></div>
              <div className="col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">内容 / 链接</label><textarea className="textarea h-32 font-mono text-sm" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
              <div className="col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">备注</label><input className="input" value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })} /></div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary">取消</button>
              <button onClick={save} className="btn-primary">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* File Detail */}
      {showFileDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">文件详情</h3>
              <button onClick={() => setShowFileDetail(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">文件名</div><div className="font-medium">{showFileDetail.fileName}</div></div>
                <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">文件类型</div><div className="font-medium">{showFileDetail.fileType}</div></div>
                <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">大小</div><div className="font-medium">{formatFileSize(showFileDetail.fileSize)}</div></div>
                <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">分类</div><div className="font-medium">{showFileDetail.category}</div></div>
              </div>
              {showFileDetail.summary && <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">摘要</div><div>{showFileDetail.summary}</div></div>}
              {showFileDetail.keyRules && <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">关键规则</div><div>{showFileDetail.keyRules}</div></div>}
              {showFileDetail.riskPoints && <div className="p-2 bg-slate-50 rounded"><div className="text-slate-500">风险点</div><div>{showFileDetail.riskPoints}</div></div>}
              <div className="flex gap-4 text-xs text-slate-500">
                <span>上传人: {showFileDetail.uploadedBy}</span>
                <span>时间: {formatDateShort(showFileDetail.uploadedAt)}</span>
                <span>解析: {showFileDetail.isParsed ? '已解析' : '待解析'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
